// src/MembersDashboard.js

import React, { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { auth, db } from "./firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

const brollOptions = [
  { file: "declutter_home.mov", title: "Declutter to Close Fast", script: "If I wanted to sell my home in 30 days... here is what I'd do..." },
  { file: "supply_demand.mp4", title: "What Drives Prices", script: "Ever wondered what really drives home prices? It all comes down to supply and demand." },
  { file: "neighborhood_value.mp4", title: "Smart Neighborhood Picks", script: "Not all neighborhoods are created equal..." },
  { file: "home_inspection.mp4", title: "Inspection Red Flags", script: "Don't miss these 3 things during a home inspection..." },
  { file: "budget_first.mp4", title: "Afford Before You Adore", script: "Before you fall in love with a home, let’s talk numbers!" },
  { file: "interest_rate.mp4", title: "When Rates Rise...", script: "Interest rates change... and when it happens, it can shake up the market!" },
  { file: "loan_types.mp4", title: "Which Loan is Right?", script: "Just the thought of choosing the right mortgage can feel overwhelming..." },
  { file: "prelist_inspection.mp4", title: "Seller Inspection Advantage", script: "Let’s talk about how seller pre-list inspections can be a real game changer..." },
  { file: "buyer_anxiety.mp4", title: "Dream Home Anxiety", script: "Worried about buying your dream home? You're not alone..." },
];

export default function MembersDashboard() {
  const [agentName, setAgentName] = useState("");
  const [email, setEmail] = useState("");
  const [voiceFile, setVoiceFile] = useState(null);
  const [selectedBroll, setSelectedBroll] = useState([]);
  const [downloadUrls, setDownloadUrls] = useState([]);
  const [userUID, setUserUID] = useState(null);
  const [usageBlocked, setUsageBlocked] = useState(false);
  const [usageMessage, setUsageMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [rating, setRating] = useState("");
  const [comment, setComment] = useState("");
  const [recommend, setRecommend] = useState("");
  const [showFeedbackForm, setShowFeedbackForm] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) setUserUID(user.uid);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const checkLastUsage = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const email = userSnap.data().email?.toLowerCase();
        if (email === "realsautomated@gmail.com") return;

        const lastUsed = userSnap.data().lastUsed;
        if (lastUsed?.toDate) {
          const lastUsedDate = lastUsed.toDate();
          const now = new Date();
          const diffDays = Math.floor((now - lastUsedDate) / (1000 * 60 * 60 * 24));
          if (diffDays < 7) {
            setUsageBlocked(true);
            setUsageMessage(`⏳ You can only generate reels once every 7 days. Please come back in ${7 - diffDays} day(s).`);
          }
        }
      }
    };

    checkLastUsage();
  }, []);

  const toggleBroll = (file) => {
    setSelectedBroll((prev) => {
      if (prev.includes(file)) return prev.filter((f) => f !== file);
      else if (prev.length < 7) return [...prev, file];
      else {
        alert("You can only select 7 b-rolls.");
        return prev;
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (usageBlocked) return alert(usageMessage);
    if (!voiceFile || selectedBroll.length !== 7 || !agentName || !email) return alert("🚫 Complete all fields and select 7 b-rolls.");
    setIsLoading(true);

    const formData = new FormData();
    formData.append("voice", voiceFile);
    formData.append("name", agentName);
    formData.append("email", email);
    formData.append("selectedBroll", JSON.stringify(selectedBroll));

    try {
      const uploadRes = await fetch("https://reel-backend-y11o.onrender.com/upload", { method: "POST", body: formData });
      const uploadData = await uploadRes.json();
      alert(uploadData.message || "✅ Upload complete!");

      const processRes = await fetch("https://reel-backend-y11o.onrender.com/process-reels", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ agentName, agentEmail: email }),
      });
      const processData = await processRes.json();

      if (processData.downloadUrls) {
        setDownloadUrls(processData.downloadUrls);
      }

      const user = auth.currentUser;
      if (user) await setDoc(doc(db, "users", user.uid), { lastUsed: serverTimestamp() }, { merge: true });
    } catch (err) {
      console.error("❌ Upload or processing failed:", err);
      alert("Something went wrong.");
    } finally {
      setIsLoading(false);
    }
  };

  const VoiceDrop = () => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: { "audio/mpeg": [".mp3"], "audio/wav": [".wav"] },
      onDrop: (acceptedFiles) => setVoiceFile(acceptedFiles[0])
    });

    return (
      <div {...getRootProps()} className="border border-dashed p-4 rounded-md bg-white text-sm shadow hover:border-purple-400 cursor-pointer">
        <input {...getInputProps()} />
        {voiceFile ? <p>🎤 Voice ready: {voiceFile.name}</p> : <p>{isDragActive ? "Drop the file here..." : "🎤 Drag and drop or click to upload voice (MP3/WAV)"}</p>}
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      {isLoading && <div className="fixed inset-0 bg-black bg-opacity-60 text-white text-center p-10 z-50">⏳ Generating reels...</div>}
      <div className="bg-white rounded-xl shadow p-6 space-y-6">
        <h1 className="text-3xl font-bold">🎬 Weekly Reel Generator</h1>

        {usageBlocked && <div className="bg-yellow-100 p-4 border-l-4 border-yellow-400 text-yellow-700">{usageMessage}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <input placeholder="Agent Name" value={agentName} onChange={(e) => setAgentName(e.target.value)} className="w-full p-2 border rounded-md" />
          <input placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-2 border rounded-md" />
          <VoiceDrop />

          <div>
            <label className="block font-semibold mb-2">Select 7 B-Roll Clips</label>
            <div className="grid md:grid-cols-3 gap-3">
              {brollOptions.map((b) => (
                <button key={b.file} type="button" onClick={() => toggleBroll(b.file)} className={`border p-3 rounded-md text-left ${selectedBroll.includes(b.file) ? "bg-purple-100 border-purple-400" : "hover:bg-gray-100"}`}>
                  🎞️ <strong>{b.title}</strong>
                  <br />
                  <span className="text-xs text-gray-600">{b.script}</span>
                </button>
              ))}
            </div>
          </div>

          <button type="submit" disabled={isLoading} className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg w-full font-semibold">
            {isLoading ? "Processing..." : "🚀 Generate Reels Now"}
          </button>
        </form>

        {downloadUrls.length > 0 && (
          <div className="mt-6">
            <h2 className="text-xl font-bold mb-2">📥 Download Your Reels</h2>
            <ul className="list-disc list-inside space-y-1">
              {downloadUrls.map((url, idx) => (
                <li key={idx}>
                  <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Reel #{idx + 1}</a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="pt-8">
          <h2 className="text-xl font-bold mb-2">📂 Bonus Scripts + Marketing Captions</h2>
          <p className="text-sm text-gray-700 mb-2">We update this folder weekly with new CTAs and social-ready content.</p>
          <a
            href="https://drive.google.com/drive/folders/YOUR_FOLDER_ID_HERE"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-blue-600 text-white px-5 py-2 rounded-md shadow hover:bg-blue-700"
          >
            🔗 Open Resource Folder
          </a>
        </div>
      </div>
    </div>
  );
}
